import { type ApiClient } from '../api/apiClient';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { sanitizeControlCharactersDeep } from '../utils/controlCharacters';
import { 
  mapPositionStatusEnum,
  mapInterviewStatusEnum,
  mapInterviewResultEnum,
  // mapQuestionTypeEnum удален
} from '../utils/enumMapper';
// Импортируем типы напрямую из сгенерированных моделей
import type { Position } from '../../generated-src/client/positions/api';
import type { PositionCreateRequest } from '../../generated-src/client/positions/api';
import type { PositionUpdateRequest } from '../../generated-src/client/positions/api';
import type { Question } from '../../generated-src/client/positions/api';
import type { QuestionCreateRequest } from '../../generated-src/client/interviews/api';
import { PositionStatusEnum } from '../../generated-src/client/positions/api';
import type { BaseQuestionFields } from '../../generated-src/client/positions/api';
import type { PositionDataGenerationRequest } from '../../generated-src/client/generation/api';
import type { PositionDataGenerationResponse } from '../../generated-src/client/generation/api';
import { Configuration } from '../../generated-src/client/account/configuration';
import { AuthApi } from '../../generated-src/client/account/api';
import { AccountApi, ListAccountsRole } from '../../generated-src/client/account/api';
import type { AccountResponse } from '../../generated-src/client/account/api';

import { InterviewsApi } from '../../generated-src/client/interviews/api';
import { PositionsApi } from '../../generated-src/client/positions/api';
import { QuestionsApi } from '../../generated-src/client/interviews/api';
import { TeamsApi } from '../../generated-src/client/teams/api';
import { StatisticsApi } from '../../generated-src/client/statistics/api';
import { GenerationApi } from '../../generated-src/client/generation/api';
import { InterviewSettingsApi } from '../../generated-src/client/interview-settings/api';
import { BrandingApi } from '../../generated-src/client/branding/api';
import { CompaniesApi } from '../../generated-src/client/companies/api';
import { CompanyCreditsApi } from '../../generated-src/client/company-credits/api';
import { ReportsApi } from '../../generated-src/client/reports/api';
import { ComplaintsApi } from '../../generated-src/client/complaints/api';
import {
    AccountTelegramApi,
    AccountNotificationPreferencesApi,
} from '../../generated-src/client/account-notifications/api';
import type { InterviewListItem as InterviewItem } from '../../generated-src/client/interviews/api';

// Локальные типы для работы с пользователями
interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  teamId?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Type definitions for paginated responses (local shapes)
interface PositionsPaginatedResponse { content?: Position[]; totalElements?: number; totalPages?: number; number?: number; size?: number }
interface InterviewsPaginatedResponse { content?: InterviewItem[]; totalElements?: number; totalPages?: number; number?: number; size?: number }
interface RecruitmentResultsReportFilters { period?: string; search?: string; positionId?: number; maxRows?: number }

/**
 * Основной сервис для работы с API
 * Предоставляет методы для аутентификации и работы с различными доменами
 */
class ApiService {
  private lastToken: string | null = null;

  /**
   * Получить аутентифицированный API клиент с текущим токеном
   * @returns {ApiClient} Клиент с настроенной аутентификацией
   */
  getAuthenticatedClient(): ApiClient {
    const currentToken = useAuthStore.getState().token;
    
    console.log('🔑 getApiClient - token length:', currentToken?.length || 0);
    console.log('🔑 getApiClient - isAuth:', useAuthStore.getState().isAuth);

    // Создаем новый клиент с текущим токеном
    const config = new Configuration({
      basePath: (process.env.REACT_APP_API_BASE_URL || '/api/v1').toString().trim().replace(/\/+$/, ''),
      accessToken: async () => {
        const token = useAuthStore.getState().token;
        return token || '';
      }
    });

    console.log('🔑 config:', config);
    console.log('🔑 basePath:', config.basePath);

    const apiClient = {
      auth: new AuthApi(config),
      account: new AccountApi(config),
      interviews: new InterviewsApi(config),
      positions: new PositionsApi(config),
      questions: new QuestionsApi(config),
      teams: new TeamsApi(config),
      statistics: new StatisticsApi(config),
      generation: new GenerationApi(config),
      interviewSettings: new InterviewSettingsApi(config),
      branding: new BrandingApi(config),
      companies: new CompaniesApi(config),
      companyCredits: new CompanyCreditsApi(config),
      reports: new ReportsApi(config),
      complaints: new ComplaintsApi(config),
      accountTelegram: new AccountTelegramApi(config),
      accountNotificationPreferences: new AccountNotificationPreferencesApi(config),
    };

    console.log('🔑 API клиент создан:', apiClient);
            console.log('🔑 generation API:', apiClient.generation);

    return apiClient;
  }

  /**
   * Получить API клиент без аутентификации для публичных эндпоинтов
   * @returns {ApiClient} Клиент без токена аутентификации
   */
  getPublicApiClient(): ApiClient {
    // Создаем новый клиент без токена для публичных эндпоинтов
    const config = new Configuration({
      basePath: (process.env.REACT_APP_API_BASE_URL || '/api/v1').toString().trim().replace(/\/+$/, '')
      // Не устанавливаем accessToken для публичных эндпоинтов
    });

    return {
      auth: new AuthApi(config),
      account: new AccountApi(config),
      interviews: new InterviewsApi(config),
      positions: new PositionsApi(config),
      questions: new QuestionsApi(config),
      teams: new TeamsApi(config),
      statistics: new StatisticsApi(config),
      generation: new GenerationApi(config),
      interviewSettings: new InterviewSettingsApi(config),
      branding: new BrandingApi(config),
      companies: new CompaniesApi(config),
      companyCredits: new CompanyCreditsApi(config),
      reports: new ReportsApi(config),
      complaints: new ComplaintsApi(config),
      accountTelegram: new AccountTelegramApi(config),
      accountNotificationPreferences: new AccountNotificationPreferencesApi(config),
    };
  }

  // === BRANDING ===
  /**
   * Обновляет настройки брендинга компании
   * @param brandingData - Данные брендинга (цвета, логотип, название)
   * @returns Promise<any> - Обновленные настройки брендинга
   */
  async updateMyCompanyBranding(brandingData: any): Promise<any> {
    const response = await this.getAuthenticatedClient().branding.updateMyCompanyBranding(brandingData);
    return response.data;
  }

  /**
   * Получает текущие настройки брендинга компании
   * @returns Promise<any> - Настройки брендинга (цвета, логотип, название)
   */
  async getMyCompanyBranding(): Promise<any> {
    const response = await this.getAuthenticatedClient().branding.getMyCompanyBranding();
    return response.data;
  }

  /**
   * Загружает логотип компании
   * @param logoFile - Файл логотипа
   * @returns Promise<any> - URL загруженного логотипа
   */
  async uploadMyCompanyLogo(logoFile: File): Promise<any> {
    const token = useAuthStore.getState().token;
    const formData = new FormData();
    formData.append('logoFile', logoFile);
    const response = await axios.post(this.resolveUrl('/companies/my/branding/logo'), formData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  }

  async deleteMyCompanyLogo(): Promise<any> {
    const response = await this.getAuthenticatedClient().branding.deleteMyCompanyLogo();
    return response.data;
  }

  // === COMPANIES ===
  async getMyCompany(): Promise<any> {
    const response = await this.getAuthenticatedClient().companies.getMyCompany();
    return response.data;
  }

  // === AUTHENTICATION ===
  // async login(email: string, password: string): Promise<AuthResponse> {
  //   const loginRequest: LoginRequest = { email, password };
  //   // For login, we don't need token yet, so we use the public client
  //   const response = await this.getPublicApiClient().auth.login(loginRequest);
  //   return response.data;
  // }

  // Force refresh API client (useful after login/logout)
  refreshApiClient() {
    console.log('🔍 refreshApiClient - No longer needed, using global apiClient');
    // The global apiClient automatically gets the current token from the store
  }

  async logout(): Promise<void> {
    // Очищаем токен из store
    useAuthStore.getState().logout();
  }

  // === ACCOUNT ===
    async getAccount(): Promise<User> {
    console.log('🔍 getAccount called');
    console.log('🔍 Token check:', useAuthStore.getState().token ? 'present' : 'missing');
    const token = useAuthStore.getState().token;

    try {
      console.log('🔍 Calling getCurrentAccount...');
      const client = this.getAuthenticatedClient();
      console.log('🔍 getAuthenticatedClient created:', !!client);
      console.log('🔍 account API:', !!client.account);
      
      const response = await client.account.getCurrentAccount();
      console.log('🔍 getCurrentAccount response:', response);
      console.log('🔍 response.data:', response.data);
      return response.data as any;
    } catch (error: any) {
      console.error('🔍 getAccount error:', error);
      console.error('🔍 Error response:', error.response);
      console.error('🔍 Error message:', error.message);
      console.error('🔍 Error status:', error.response?.status);
      console.error('🔍 Error headers:', error.response?.headers);
      throw error;
    }
  }

  async getAccountById(id: number): Promise<User> {
    try {
      const response = await this.getAuthenticatedClient().account.getAccountById(id);
      return response.data as any;
    } catch (error: any) {
      console.error('getAccountById error:', error);
      throw error;
    }
  }

  async updateAccount(userData: any): Promise<User> {
    console.log('🔍 apiService.updateAccount: Starting with userData:', userData);
    console.log('🔍 apiService.updateAccount: Token check:', useAuthStore.getState().token ? 'present' : 'missing');
    
    try {
      const response = await this.getAuthenticatedClient().account.updateCurrentAccount(userData);
      console.log('🔍 apiService.updateAccount: Response received:', response);
      console.log('🔍 apiService.updateAccount: Response data:', response.data);
      return response.data as any;
    } catch (error: any) {
      console.error('❌ apiService.updateAccount: Error occurred:', error);
      console.error('❌ apiService.updateAccount: Error response:', error.response);
      console.error('❌ apiService.updateAccount: Error status:', error.response?.status);
      console.error('❌ apiService.updateAccount: Error data:', error.response?.data);
      throw error;
    }
  }

  // === POSITIONS ===
  /**
   * Получает список вакансий с фильтрацией и пагинацией
   * @param params - Параметры фильтрации и пагинации
   * @param params.status - Статус вакансии (ACTIVE, DRAFT, CLOSED)
   * @param params.search - Поисковый запрос
   * @param params.page - Номер страницы (начиная с 1)
   * @param params.size - Размер страницы
   * @param params.owner - Фильтр по владельцу
   * @returns Promise с пагинированным списком вакансий
   */
  async getPositions(params?: {
    status?: PositionStatusEnum;
    search?: string;
    page?: number;
    size?: number;
    owner?: string;
  }): Promise<{ items: Position[]; total: number }> {
    try {
      console.log('🔍 getPositions called with params:', params);
      console.log('🔍 getPositions token check:', useAuthStore.getState().token ? 'present' : 'missing');
      
      const page0 = params?.page != null ? Math.max(0, params.page - 1) : 0;
      console.log('🔍 getPositions calling listPositions with:', {
        status: params?.status,
        search: params?.search,
        owner: params?.owner,
        page: page0,
        size: params?.size
      });
      
      const response = await this.getAuthenticatedClient().positions.listPositions(
        params?.status,
        params?.search,
        params?.owner,
        page0,
        params?.size
      );

      const data = response.data as PositionsPaginatedResponse;

      // OpenAPI spec defines Spring Boot Page format with content, totalElements, etc.
      if (data.content && Array.isArray(data.content)) {
        // Безопасно маппим enum'ы для каждой позиции
        const items = data.content.map(position => ({
          ...position,
          status: mapPositionStatusEnum(position.status)
        }));

        return {
          items: items || [],
          total: data.totalElements || 0
        };
      } else {
        // Fallback for unexpected format
        console.error('Unexpected response format:', data);
        return {
          items: [],
          total: 0
        };
      }
    } catch (error: any) {
      console.error('getPositions error:', error);

      // If 400 error, try without status parameter
      if (error.response?.status === 400 && params?.status) {
        try {
          const page0 = params?.page != null ? Math.max(0, params.page - 1) : 0;
          const retryResponse = await this.getAuthenticatedClient().positions.listPositions(
            undefined,
            params?.search,
            params?.owner,
            page0,
            params?.size
          );

          const retryData = retryResponse.data as PositionsPaginatedResponse;

          if (retryData.content && Array.isArray(retryData.content)) {
            // Безопасно маппим enum'ы для каждой позиции
            const items = retryData.content.map(position => ({
              ...position,
              status: mapPositionStatusEnum(position.status)
            }));

            return {
              items: items || [],
              total: retryData.totalElements || 0
            };
          } else {
            return {
              items: [],
              total: 0
            };
          }
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
          throw retryError;
        }
      }

      throw error;
    }
  }

  async getPosition(id: number): Promise<Position> {
    const response = await this.getAuthenticatedClient().positions.getPosition(id);
    return response.data;
  }

  /**
   * Создает новую вакансию
   * @param positionData - Данные для создания вакансии
   * @returns Promise<Position> - Созданная вакансия
   */
  async createPosition(positionData: PositionCreateRequest): Promise<Position> {
    console.log('🔍 createPosition - Starting with token:', useAuthStore.getState().token ? 'present' : 'missing');
    try {
      const sanitizedPositionData = sanitizeControlCharactersDeep(positionData, 'position');
      const response = await this.getAuthenticatedClient().positions.createPosition(sanitizedPositionData);
      console.log('🔍 createPosition - Success');
      return response.data;
    } catch (error: any) {
      console.error('🔍 createPosition - Error:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  async updatePosition(id: number, positionData: PositionUpdateRequest): Promise<Position> {
    const sanitizedPositionData = sanitizeControlCharactersDeep(positionData, 'position');
    const response = await this.getAuthenticatedClient().positions.updatePosition(id, sanitizedPositionData);
    return response.data;
  }

  /**
   * TASKNEIROKLYUCH-659: regenerate ai_requirements_weighted for an existing position.
   * Triggered by the "AI" button in RequirementsPanel. Backend overwrites whatever is stored —
   * the caller must have already confirmed with the user (manual edits are lost).
   *
   * @returns the new JSON string persisted to ai_requirements_weighted.
   * @throws on generation failure (422) — callers should surface the error to the user.
   */
  async regeneratePositionAiRequirements(id: number): Promise<string> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${id}/regenerate-ai-requirements`;
    const response = await axios.post<{ aiRequirementsWeighted: string }>(url, null, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data.aiRequirementsWeighted;
  }

  /**
   * Stateless-генерация взвешенных требований по сырым полям (без сохранения позиции).
   * Используется на шаге "Далее: требования" при создании новой вакансии.
   * Использует тот же backend-метод и промпт, что и `regeneratePositionAiRequirements` —
   * идентичное поведение с "AI" кнопкой на странице существующей вакансии.
   */
  async generateAiRequirementsStateless(payload: {
    title?: string;
    jobDescription: string;
    level?: string;
    skillGroups?: string;
  }): Promise<string> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/ai/generate-requirements`;
    const response = await axios.post<{ aiRequirementsWeighted: string }>(url, payload, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data.aiRequirementsWeighted;
  }

  // === FR-E2E-6: CRUD привязок вакансий из любых источников ===

  async getSourceLinks(positionId: number): Promise<Array<{ id: number; source: string; externalVacancyId: string; createdAt: string }>> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${positionId}/source-links`;
    const response = await axios.get(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  }

  async addSourceLink(positionId: number, source: string, externalVacancyId: string): Promise<{ id: number; source: string; externalVacancyId: string; createdAt: string }> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${positionId}/source-links`;
    const response = await axios.post(url, { source, externalVacancyId }, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  async deleteSourceLink(positionId: number, linkId: number): Promise<void> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${positionId}/source-links/${linkId}`;
    await axios.delete(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  }

  // TASKNEIROKLYUCH-435 FR-1: получить externalVacancyId из source-links для указанного источника.
  // Используется при создании рассылки в релевантере, чтобы передать estaffVacancyId/hhVacancyId
  // в payload createCampaign по аналогии с существующим хранением через PositionSourceLink.
  async resolveSourceVacancyId(positionId: number, source: 'ESTAFF' | 'HH'): Promise<string | undefined> {
    try {
      const links = await this.getSourceLinks(positionId);
      const link = links.find((l) => l.source === source);
      return link?.externalVacancyId || undefined;
    } catch {
      return undefined;
    }
  }

  async updatePositionAiScreeningFlag(id: number, enabled: boolean): Promise<void> {
    const token = useAuthStore.getState().token;
    const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${id}/ai-screening`;
    await axios.patch(
      url,
      { aiScreeningEnabled: enabled },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  async updatePositionStatus(id: number, status: PositionStatusEnum): Promise<Position> {
    try {
      console.log(`🔍 apiService.updatePositionStatus вызван с id=${id}, status=${status}`);
      
      // Получаем токен
      const token = useAuthStore.getState().token;
      
      // Используем новый PATCH endpoint для изменения статуса
      const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/positions/${id}/status?status=${status}`;
      console.log(`🌐 URL запроса:`, url);
      
      console.log(`📡 Отправляем PATCH запрос...`);
      const response = await axios.patch(
        url,
        {}, // Пустое тело для PATCH запроса
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log(`✅ Ответ получен:`, response.status, response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ updatePositionStatus error:', error);
      console.error('❌ Статус ответа:', error.response?.status);
      console.error('❌ Данные ответа:', error.response?.data);
      console.error('❌ Сообщение ошибки:', error.message);
      throw error;
    }
  }


  async getPositionStats(id: number): Promise<any> {
    // Универсальная статистика по вакансиям (пер-позиционные недоступны)
    // Используем общую статистику, так как пер-позиционные недоступны
    const response = await this.getAuthenticatedClient().statistics.getOverviewStats('all');
    return response.data;
  }

  // getPositionPublicLink method removed - functionality not needed per business requirements

  // === CANDIDATES ===
  async getApplicantById(id: string): Promise<any> {
    try {
      // Используем правильный эндпоинт из старого приложения
      const response = await this.get(`/applicants/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('getApplicantById error:', error);
      throw error;
    }
  }

  // === INTERVIEWS ===
  async getInterviews(params?: {
    positionId?: number;
    candidateId?: number;
    status?: string;
    result?: string;
    search?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<{ items: InterviewItem[]; total: number }> {
    try {
      const page0 = params?.page != null ? Math.max(0, params.page - 1) : 0;
      const response = await this.getAuthenticatedClient().interviews.listInterviews(
        params?.positionId,
        params?.candidateId,
        params?.status as any,
        params?.result as any,
        params?.search,
        page0,
        params?.size,
        params?.sort
      );

      const data = response.data as InterviewsPaginatedResponse;

      // OpenAPI spec defines Spring Boot Page format
      if (data.content && Array.isArray(data.content)) {
        // Безопасно маппим enum'ы для каждого интервью
        const items = data.content.map(interview => ({
          ...interview,
          status: mapInterviewStatusEnum(interview.status),
          result: interview.result ? mapInterviewResultEnum(interview.result) : undefined
        }));

        return {
          items: items || [],
          total: data.totalElements || 0
        };
      } else {
        console.error('Unexpected response format:', data);
        return {
          items: [],
          total: 0
        };
      }
    } catch (error: any) {
      console.error('getInterviews error:', error);

      // If 400 error, try without optional parameters
      if (error.response?.status === 400) {
        try {
          const page0 = params?.page != null ? Math.max(0, params.page - 1) : 0;
          const retryResponse = await this.getAuthenticatedClient().interviews.listInterviews(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            page0,
            params?.size
          );

          const retryData = retryResponse.data as InterviewsPaginatedResponse;

          if (retryData.content && Array.isArray(retryData.content)) {
            // Безопасно маппим enum'ы для каждого интервью
            const items = retryData.content.map(interview => ({
              ...interview,
              status: mapInterviewStatusEnum(interview.status),
              result: interview.result ? mapInterviewResultEnum(interview.result) : undefined
            }));

            return {
              items: items || [],
              total: retryData.totalElements || 0
            };
          } else {
            return {
              items: [],
              total: 0
            };
          }
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
          throw retryError;
        }
      }

      throw error;
    }
  }

  async getInterview(id: number): Promise<any> {
    const response = await this.getAuthenticatedClient().interviews.getInterview(id);
    return response.data;
  }

  async getInterviewAnswers(id: number): Promise<any> {
    const response = await this.getAuthenticatedClient().interviews.getInterviewAnswers(id);
    return response.data;
  }

  /**
   * Создает новое интервью для кандидата
   * @param interviewData - Данные интервью
   * @param interviewData.positionId - ID вакансии
   * @param interviewData.candidateEmail - Email кандидата
   * @param interviewData.firstName - Имя кандидата (опционально)
   * @param interviewData.lastName - Фамилия кандидата (опционально)
   * @returns Promise<InterviewItem> - Созданное интервью
   */
  async createInterview(interviewData: {
    positionId: number;
    candidateEmail: string;
    firstName?: string;
    lastName?: string;
  }): Promise<InterviewItem> {
    console.log('🔍 createInterview - interviewData:', interviewData);

    // Проверяем токен авторизации
    const currentToken = useAuthStore.getState().token;
    if (!currentToken) {
      throw new Error('Токен авторизации отсутствует. Необходимо войти в систему.');
    }
    
    
    const response = await this.getAuthenticatedClient().interviews.createInterview(interviewData);

    console.log('🔍 createInterview - response:', response.data);
    return response.data as InterviewItem;
  }

  // Получение всех интервью (без фильтрации по позиции) постранично
  async getAllInterviewsPaged(page: number = 1, size: number = 10, opts?: { status?: string; result?: string; search?: string; sort?: string }): Promise<{ items: InterviewItem[]; total: number }> {
    const page0 = Math.max(0, page - 1);
    const response = await this.getAuthenticatedClient().interviews.listInterviews(
      undefined, // positionId
      undefined, // candidateId
      opts?.status as any,
      opts?.result as any,
      opts?.search,
      page0,
      size,
      opts?.sort
    );

    const data = response.data as InterviewsPaginatedResponse;
    const pageItems = (data.content && Array.isArray(data.content)) ? data.content : [];

    const mapped = pageItems.map(interview => ({
      ...interview,
    }));

    return {
      items: mapped,
      total: data.totalElements || 0,
    };
  }

  async getPositionInterviewsPaged(positionId: number, page: number = 1, size: number = 10, opts?: { status?: string; result?: string; search?: string; sort?: string }): Promise<{ items: InterviewItem[]; total: number }> {
    const page0 = Math.max(0, page - 1);
    const response = await this.getAuthenticatedClient().interviews.listPositionInterviews(
      positionId,
      opts?.status as any,
      opts?.result as any,
      opts?.search,
      page0,
      size,
      opts?.sort
    );

    const data = response.data as InterviewsPaginatedResponse;
    const pageItems = (data.content && Array.isArray(data.content)) ? data.content : [];

    const mapped = pageItems.map(interview => ({
      ...interview,
      status: mapInterviewStatusEnum(interview.status),
      result: interview.result ? mapInterviewResultEnum(interview.result) : undefined
    }));

    return {
      items: mapped,
      total: typeof data.totalElements === 'number' ? data.totalElements : mapped.length
    };
  }

  async createConversationMapping(interviewId: number, conversationId: string): Promise<void> {
    // TODO: Implement when createConversationMapping method is available in API
    console.warn('createConversationMapping method not available in API yet');
    throw new Error('Method not implemented');
  }

  // === QUESTIONS ===
  async updatePositionQuestionsAndSettings(positionId: number, payload: import('../../generated-src/client/positions/api').PositionQuestionsAndSettingsUpdateRequest): Promise<any> {
    const response = await this.getAuthenticatedClient().positions.updatePositionQuestionsAndSettings(positionId, payload as any);
    return response.data;
  }

  async getQuestions(positionId: number): Promise<{ questions: Question[]; interviewSettings: { answerTime?: number; language?: string; saveAudio?: boolean; saveVideo?: boolean; randomOrder?: boolean; minScore?: number } }> {
    try {
      // Используем правильный эндпоинт для получения вопросов с настройками
      const response = await this.getAuthenticatedClient().positions.getPositionQuestionsAndSettings(positionId);
      const data = response.data;

      // Проверяем структуру ответа
      if (data.questions && Array.isArray(data.questions)) {
        // Все вопросы теперь текстовые
        const questions = data.questions.map((question: any) => ({
          ...question,
          type: 'TEXT'
        }));

        return {
          questions: questions || [],
          interviewSettings: {
            ...data.interviewSettings,
            minScore: (data.interviewSettings as any)?.minScore || 0
          } as any
        };
      } else {
        console.error('Unexpected response format for questions:', data);
        return {
          questions: [],
          interviewSettings: {
            minScore: 0
          }
        };
      }
    } catch (error: any) {
      console.error('getQuestions error:', error);

      // Fallback: попробуем старый эндпоинт
      try {
        console.log('Trying fallback endpoint...');
        const fallbackResponse = await this.getAuthenticatedClient().positions.getPositionQuestionsAndSettings(positionId);
        const fallbackData = fallbackResponse.data;

        // Новый эндпоинт возвращает PositionQuestionsResponse
        if (fallbackData.questions && Array.isArray(fallbackData.questions)) {
          console.log('Using fallback endpoint with questions array');
          // Все вопросы теперь текстовые
          const questions = fallbackData.questions.map((question: any) => ({
            ...question,
            type: 'TEXT'
          }));

          return {
            questions: questions || [],
            interviewSettings: {
              ...fallbackData.interviewSettings,
              minScore: (fallbackData.interviewSettings as any)?.minScore || 0
            } as any
          };
        } else {
          console.error('Fallback endpoint also failed with unexpected format:', fallbackData);
          return {
            questions: [],
            interviewSettings: {
              minScore: 0
            }
          };
        }
      } catch (fallbackError) {
        console.error('Fallback endpoint also failed:', fallbackError);
        return {
          questions: [],
          interviewSettings: {
            minScore: 0
          }
        };
      }
    }
  }

  async createQuestion(positionId: number, questionData: QuestionCreateRequest): Promise<Question> {
    console.log('🔍 createQuestion - Starting with token:', useAuthStore.getState().token ? 'present' : 'missing');
    console.log('🔍 createQuestion - Position ID:', positionId);


    try {
      const response = await this.getAuthenticatedClient().questions.createPositionQuestion(positionId, questionData);
      console.log('🔍 createQuestion - Success');
      return response.data;
    } catch (error: any) {
      console.error('🔍 createQuestion - Error:', error.response?.status, error.response?.data);
      console.error('🔍 createQuestion - Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      throw error;
    }
  }

  async updateQuestion(id: number, questionData: BaseQuestionFields): Promise<Question> {
    const response = await this.getAuthenticatedClient().questions.updateQuestion(id, questionData);
    return response.data;
  }

  async deleteQuestion(id: number): Promise<void> {
    await this.getAuthenticatedClient().questions.deleteQuestion(id);
  }

  // === TEAM & USERS ===
  // Методы для работы с пользователями удалены - соответствующий API недоступен

  // === TEAM MANAGEMENT ===
  /**
   * Создает новую команду
   * @param teamData - Данные команды
   * @param teamData.name - Название команды
   * @param teamData.description - Описание команды (опционально)
   * @param teamData.ownerId - ID владельца команды
   * @returns Promise<any> - Созданная команда
   */
  async createTeam(teamData: { name: string; description?: string | null; ownerId: number }): Promise<any> {
    try {
      const response = await this.getAuthenticatedClient().teams.createTeam({
        name: teamData.name,
        description: teamData.description || '',
        ownerId: teamData.ownerId
      });
      return response.data;
    } catch (error: any) {
      console.error('createTeam error:', error);
      throw error;
    }
  }

  async getTeam(teamId: number): Promise<any> {
    try {
      const response = await this.getAuthenticatedClient().teams.getTeam(teamId);
      return response.data;
    } catch (error: any) {
      console.error('getTeam error:', error);
      throw error;
    }
  }

  async updateTeam(teamId: number, teamData: { name: string; description?: string | null; ownerId: number }): Promise<any> {
    try {
      const response = await this.getAuthenticatedClient().teams.patchTeam(teamId, {
        name: teamData.name,
        description: teamData.description || '',
        ownerId: teamData.ownerId
      });
      return response.data;
    } catch (error: any) {
      console.error('updateTeam error:', error);
      throw error;
    }
  }

  async listTeams(page: number = 0, size: number = 20): Promise<any> {
    try {
      const response = await this.getAuthenticatedClient().teams.listTeams(undefined, page, size);
      return response.data;
    } catch (error: any) {
      console.error('listTeams error:', error);
      throw error;
    }
  }

  async getTeamUsers(teamId: number): Promise<any[]> {
    try {
      const response = await this.getAuthenticatedClient().teams.getTeamUsersList(teamId);
      return response.data;
    } catch (error: any) {
      console.error('getTeamUsers error:', error);
      return [];
    }
  }

  async getTeamUsersPaginated(teamId: number, page: number = 0, size: number = 10): Promise<{ users: any[], total: number, totalPages: number }> {
    try {
      console.log('🔍 getTeamUsersPaginated called with:', { teamId, page, size });
      
      // Используем правильный API для получения пользователей команды
      const response = await this.getAuthenticatedClient().teams.getTeamUsersList(teamId);
      
      console.log('🔍 Team users API response:', response.data);
      
      const allTeamUsers = response.data || [];
      
      // Применяем клиентскую пагинацию (так как API не поддерживает серверную пагинацию)
      const startIndex = page * size;
      const endIndex = startIndex + size;
      const paginatedUsers = allTeamUsers.slice(startIndex, endIndex);
      
      console.log('🔍 Paginated team users:', paginatedUsers);
      console.log('🔍 Pagination info:', {
        total: allTeamUsers.length,
        totalPages: Math.ceil(allTeamUsers.length / size),
        currentPage: page,
        size: size
      });
      
      return {
        users: paginatedUsers,
        total: allTeamUsers.length,
        totalPages: Math.ceil(allTeamUsers.length / size)
      };
    } catch (error: any) {
      console.error('getTeamUsersPaginated error:', error);
      return { users: [], total: 0, totalPages: 0 };
    }
  }

  async getTeamUsersByUserId(userId: number): Promise<any[]> {
    try {
      const response = await this.getAuthenticatedClient().teams.getTeamUsersByUserId(userId);
      return response.data;
    } catch (error: any) {
      console.error('getTeamUsersByUserId error:', error);
      return [];
    }
  }

  async removeUserFromTeam(teamId: number, userId: number): Promise<void> {
    try {
      await this.getAuthenticatedClient().teams.removeUserFromTeam(teamId, userId);
      console.log('Successfully removed user from team via API');
    } catch (error: any) {
      console.error('removeUserFromTeam error:', error);
      throw error;
    }
  }

  async getCompanyUsers(): Promise<AccountResponse[]> {
    try {
      // Запрашиваем только ADMIN и SUPER_ADMIN, сортируем по дате создания (новые сначала)
      const response = await this.getAuthenticatedClient().account.listAccounts(
        undefined, // search
        ListAccountsRole.ADMIN, // role - фильтруем только админов
        'ACTIVE' as any,
        undefined, // companyId
        0, // page
        100, // size - увеличиваем лимит для получения всех админов
        'createdAt,desc' // sort - сортировка по дате создания, новые сначала
      );
      
      const adminUsers = (response.data.content || []) as AccountResponse[];
      
      // Также получаем SUPER_ADMIN отдельно, так как API не поддерживает множественные роли в одном запросе
      const superAdminResponse = await this.getAuthenticatedClient().account.listAccounts(
        undefined, // search
        ListAccountsRole.SUPER_ADMIN, // role
        'ACTIVE' as any,
        undefined, // companyId
        0, // page
        100, // size
        'createdAt,desc' // sort
      );
      
      const superAdminUsers = (superAdminResponse.data.content || []) as AccountResponse[];
      
      // Объединяем и сортируем результат
      const allUsers = [...adminUsers, ...superAdminUsers];
      return allUsers.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // новые сначала
      });
    } catch (error: any) {
      console.error('getCompanyUsers error:', error);
      return [];
    }
  }

  async getRecruiterUsers(): Promise<AccountResponse[]> {
    try {
      const roles = [
        ListAccountsRole.RECRUITER,
        ListAccountsRole.ADMIN,
        ListAccountsRole.SUPER_ADMIN,
      ];

      const responses = await Promise.all(roles.map((role) => (
        this.getAuthenticatedClient().account.listAccounts(
          undefined,
          role,
          'ACTIVE' as any,
          undefined,
          0,
          100,
          'createdAt,desc',
        )
      )));

      const seen = new Set<number | string>();
      return responses
        .flatMap((response) => (response.data.content || []) as AccountResponse[])
        .filter((account) => {
          const id = account.id ?? account.email;
          if (id === undefined || id === null) return false;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });
    } catch (error: any) {
      console.error('getRecruiterUsers error:', error);
      return [];
    }
  }

  async getAssignableCompanyUsers(companyId: number): Promise<AccountResponse[]> {
    if (!Number.isInteger(companyId) || companyId <= 0) {
      return [];
    }

    const roles = [
      ListAccountsRole.OWNER,
      ListAccountsRole.ADMIN,
      ListAccountsRole.RECRUITER,
    ];

    try {
      const responses = await Promise.all(roles.map((role) => (
        this.getAuthenticatedClient().account.listAccounts(
          undefined,
          role,
          'ACTIVE' as any,
          companyId,
          0,
          100,
          'createdAt,desc',
        )
      )));

      const seen = new Set<number | string>();
      return responses
        .flatMap((response) => (response.data.content || []) as AccountResponse[])
        .filter((account) => {
          const id = account.id ?? account.email;
          if (id === undefined || id === null || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });
    } catch (error) {
      console.error('getAssignableCompanyUsers error:', error);
      throw error;
    }
  }

  // === ANALYTICS & REPORTS ===
  /**
   * Получает общую статистику системы
   * @returns Promise<any> - Статистика (количество вакансий, интервью, пользователей)
   */
  async getStats(): Promise<any> {
    const response = await this.getAuthenticatedClient().statistics.getOverviewStats('all');
    return response.data;
  }

  async getReports(): Promise<any[]> {
    const response = await this.getAuthenticatedClient().statistics.getOverviewStats('all');
    return response.data as any;
  }

  async getMonthlyReport(year: number, month: number): Promise<any> {
    // В новой статистике используем период month
    const response = await this.getAuthenticatedClient().statistics.getOverviewStats('month');
    return response.data as any;
  }

  /**
   * Обновить UI-статус интервью (scheduled | in-progress | successful | failed | error)
   */
  async updateInterviewUiStatus(interviewId: number, uiStatus: 'scheduled' | 'in-progress' | 'successful' | 'failed' | 'error'): Promise<any> {
    try {
      const token = useAuthStore.getState().token;
      const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/interviews/${interviewId}/ui-status`;
      const response = await axios.patch(url, { status: uiStatus }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      // Fallback для окружений без поддержки PATCH: пробуем POST
      if (status === 405 || status === 501) {
        try {
          const token = useAuthStore.getState().token;
          const url = `${process.env.REACT_APP_API_BASE_URL || '/api/v1'}/interviews/${interviewId}/ui-status`;
          const response = await axios.post(url, { status: uiStatus }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          return response.data;
        } catch (postErr: any) {
          console.error('updateInterviewUiStatus fallback POST error:', postErr.response?.status, postErr.response?.data);
          throw postErr;
        }
      } else {
        console.error('updateInterviewUiStatus error:', error.response?.status, error.response?.data);
        throw error;
      }
    }
  }

  // === AI ===
  // Методы для работы с AI удалены - соответствующий API недоступен

  /**
   * Генерирует данные для вакансии с помощью AI
   * @param description - Описание вакансии
   * @param existingData - Существующие данные (опционально)
   * @returns Promise<PositionDataGenerationResponse> - Сгенерированные данные вакансии
   */
  async generatePositionData(description: string, existingData?: any): Promise<PositionDataGenerationResponse> {
    console.log('🔍 generatePositionData called with:', { description, existingData });

    const request: PositionDataGenerationRequest = {
      description,
      existingData
    };

    try {
      console.log('🔍 Получаем API клиент...');
      const apiClient = this.getAuthenticatedClient();
      console.log('🔍 API клиент получен:', apiClient);
      console.log('🔍 generation API:', apiClient.generation);

      // Используем сгенерированный API
      console.log('🔍 Вызываем generation.generatePositionData...');
      const response = await apiClient.generation.generatePositionData(request);
      console.log('🔍 generatePositionData response:', response);
      console.log('🔍 response.data:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🔍 generatePositionData error:', error);
      console.error('🔍 error.response:', error.response);
      console.error('🔍 error.message:', error.message);
      throw error;
    }
  }

  /**
   * Диалоговая генерация вакансии — отправляет историю сообщений и текущие данные.
   */
  async chatGenerateVacancy(
    messages: { role: string; content: string }[],
    currentData: any,
    brandingContext?: { companyName?: string; benefits?: string; industryList?: string[]; specializationList?: string[] }
  ): Promise<any> {
    try {
      const aiAgentBaseUrl = (process.env.REACT_APP_AI_AGENT_API_URL || process.env.REACT_APP_API_BASE_URL || '/api/v1').toString().trim().replace(/\/+$/, '');
      const token = useAuthStore.getState().token;

      const response = await axios.post(
        `${aiAgentBaseUrl}/generation/vacancy/chat`,
        { messages, currentData, brandingContext },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('chatGenerateVacancy error:', error.response?.data || error.message);
      throw error;
    }
  }

  async generatePositionQuestions(positionDescription: string, questionsCount: number = 5): Promise<string[]> {
    console.log('generatePositionQuestions called with:', { positionDescriptionLength: positionDescription?.length || 0, questionsCount });

    try {
      const token = useAuthStore.getState().token;
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const url = `${baseUrl}/generation/position/questions/body`;

      const response = await axios.post(
        url,
        {
          positionDescription,
          questionsCount
        },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('generatePositionQuestions response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('generatePositionQuestions error:', error);
      throw error;
    }
  }

  async generateQuestion(currentQuestion: string, newDescription: string): Promise<string> {
    console.log('generateQuestion called with:', { currentQuestion, newDescription });

    try {
      // Используем сгенерированный API
      const response = await this.getAuthenticatedClient().generation.generateQuestion(currentQuestion, newDescription);
      console.log('generateQuestion response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('generateQuestion error:', error);
      throw error;
    }
  }

  async generateDescription(positionDescription: string): Promise<string> {
    console.log('generateDescription called with:', { positionDescription });

    try {
      // Используем сгенерированный API
      const response = await this.getAuthenticatedClient().generation.generateDescription(positionDescription);
      console.log('generateDescription response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('generateDescription error:', error);
      throw error;
    }
  }

  /**
   * Транскрибирует аудио файл в текст
   * @param audioFile - Аудио файл для транскрипции
   * @returns Promise<{ transcript: string }> - Транскрибированный текст
   */
  async transcribeAudio(audioFile: File): Promise<{ transcript: string }> {
    console.log('=== TRANSCRIBE AUDIO SERVICE START ===');
    console.log('Audio file details:', {
      name: audioFile.name,
      size: audioFile.size,
      type: audioFile.type,
      lastModified: audioFile.lastModified
    });

    // Проверяем размер файла
    if (audioFile.size === 0) {
      throw new Error('Audio file is empty');
    }

    if (audioFile.size > 50 * 1024 * 1024) { // 50MB limit
      throw new Error('Audio file is too large (max 50MB)');
    }

    try {
      console.log('Calling ai.transcribeAudio...');

      // Создаем FormData для проверки
      const formData = new FormData();
      formData.append('audio', audioFile);
      console.log('FormData created with audio file');
      console.log('FormData entries:');
      formData.forEach((value, key) => {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
      });

      // Метод transcribeAudio удален - соответствующий API недоступен
      throw new Error('AI API недоступен');
    } catch (error: any) {
      console.error('=== TRANSCRIBE AUDIO SERVICE ERROR ===');
      console.error('Error details:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      throw error;
    }
  }

  async transcribeInterviewAnswer(audioFile: File, interviewId: number, questionId: number): Promise<{ success: boolean; formattedText: string; interviewAnswerId: string }> {
    console.log('=== TRANSCRIBE INTERVIEW ANSWER SERVICE START ===');
    console.log('Audio file details:', {
      name: audioFile.name,
      size: audioFile.size,
      type: audioFile.type,
      lastModified: audioFile.lastModified
    });
    console.log('Interview ID:', interviewId, 'Question ID:', questionId);

    // Проверяем размер файла
    if (audioFile.size === 0) {
      throw new Error('Audio file is empty');
    }

    if (audioFile.size > 50 * 1024 * 1024) { // 50MB limit
      throw new Error('Audio file is too large (max 50MB)');
    }

    try {
      console.log('Calling ai.transcribeAnswerWithAI...');

      // Создаем FormData для проверки
      const formData = new FormData();
      formData.append('audioFile', audioFile);
      formData.append('interviewId', interviewId.toString());
      formData.append('questionId', questionId.toString());
      console.log('FormData created with audio file and IDs');
      console.log('FormData entries:');
      formData.forEach((value, key) => {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
      });

      // Метод transcribeAnswerWithAI удален - соответствующий API недоступен
      throw new Error('AI API недоступен');
    } catch (error: any) {
      console.error('=== TRANSCRIBE INTERVIEW ANSWER SERVICE ERROR ===');
      console.error('Error details:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      throw error;
    }
  }

  // === VOICE INTERVIEWS ===
  // createVoiceSession method removed - functionality not needed per business requirements

  // Остальные методы voice interviews будут реализованы через webhook'и
  // и не требуют прямых API вызовов с фронтенда

  // === MISSING ENDPOINTS ===

  // Positions
  async partialUpdatePosition(id: number, status: PositionStatusEnum): Promise<Position> {
    // TODO: Implement when partialUpdatePosition method is available in API
    console.warn('partialUpdatePosition method not available in API yet');
    throw new Error('Method not implemented');
  }

  // Questions
  async getPositionQuestionsWithSettings(positionId: number): Promise<any> {
    console.log('🔍 apiService.getPositionQuestionsWithSettings вызван с positionId:', positionId);
    console.log('🔍 apiClient.positions:', this.getAuthenticatedClient().positions);
    const response = await this.getAuthenticatedClient().positions.getPositionQuestionsAndSettings(positionId);
    console.log('🔍 Ответ от API:', response);
    return response.data;
  }

  // Account
  // async getUserInfo(): Promise<any> {
  //   const response = await this.getApiClient().account.getUserInfo();
  //   return response.data;
  // }

  // Analytics & Reports
  async getInterviewsStats(): Promise<any> {
    const response = await this.getAuthenticatedClient().statistics.getInterviewDetails('all');
    return response.data;
  }

  // Settings
  // Методы для работы с тарифами удалены - соответствующий API недоступен

  // Candidates
  // authCandidate удален из новой версии API
  // async authCandidate(authData: any): Promise<any> {
  //   const response = await this.getPublicApiClient().candidates.authCandidate(authData);
  //   return response.data;
  // }

  // === ACCOUNT SETTINGS ===
  async getMyAccountSettings(): Promise<any> {
    const response = await this.get('/account/me/settings');
    return response.data;
  }

  async updateMyAccountSettings(data: { aboutCompany?: string }): Promise<any> {
    const sanitizedData = sanitizeControlCharactersDeep(data, 'accountSettings');
    const response = await this.put('/account/me/settings', sanitizedData);
    return response.data;
  }

  // === GENERIC HTTP METHODS ===
  private resolveUrl(url: string): string {
    // If url is absolute (http or https), return as is
    if (/^https?:\/\//i.test(url)) return url;
    let base = (process.env.REACT_APP_API_BASE_URL || '').toString().trim();
    if (!base) {
      try {
        const isDev = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost' && window.location.port === '3000';
        base = isDev ? 'http://localhost:8080/api/v1' : '/api/v1';
      } catch {
        base = '/api/v1';
      }
    }
    base = base.replace(/\/\/+$/, '');
    if (url.startsWith('/')) return `${base}${url}`;
    return `${base}/${url}`;
  }

  async get(url: string, config?: any): Promise<any> {
    const token = useAuthStore.getState().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.get(this.resolveUrl(url), {
      ...config,
      headers: { ...headers, ...config?.headers }
    });
    return response;
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    const token = useAuthStore.getState().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.post(this.resolveUrl(url), data, {
      ...config,
      headers: { ...headers, ...config?.headers }
    });
    return response;
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    const token = useAuthStore.getState().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.put(this.resolveUrl(url), data, {
      ...config,
      headers: { ...headers, ...config?.headers }
    });
    return response;
  }

  async delete(url: string, config?: any): Promise<any> {
    const token = useAuthStore.getState().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.delete(this.resolveUrl(url), {
      ...config,
      headers: { ...headers, ...config?.headers }
    });
    return response;
  }

  // === UTILITY METHODS ===
  getInterviewStatusMap() {
    return {
      IN_PROGRESS: 'В процессе',
      FINISHED: 'Завершено',
      CANCELLED: 'Отменено'
    };
  }

  getIconMap() {
    return {
      IN_PROGRESS: '🔄',
      FINISHED: '✅',
      CANCELLED: '❌',
      HIRED: '🎉',
      REJECTED: '👎'
    };
  }

  async setPositionManager(positionId: number, managerId: number | null): Promise<any> {
    // Используем сгенерированный метод API: assignPositionManager
    // Снятие назначения: не передавать параметр (undefined), назначение: передать числовой ID
    const client = this.getAuthenticatedClient();
    const result = await client.positions.assignPositionManager(positionId, managerId === null ? undefined : managerId);
    return result.data;
  }

  // === INTERVIEW MESSAGES METHODS ===
  
  // Вступительные сообщения
  async getWelcomeMessages(): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.getWelcomeMessages();
    return response.data;
  }

  async updateWelcomeMessages(data: any): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.updateWelcomeMessages(data);
    return response.data;
  }

  // Тестовые сообщения (микрофон)
  async getTestMessages(): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.getTestMessages();
    return response.data;
  }

  async updateTestMessages(data: any): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.updateTestMessages(data);
    return response.data;
  }

  // Завершающие сообщения
  async getCompletionMessages(): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.getCompletionMessages();
    return response.data;
  }

  async updateCompletionMessages(data: any): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.updateCompletionMessages(data);
    return response.data;
  }

  // Вопросы о компании
  async getCompanyQuestions(): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.getCompanyQuestions();
    return response.data;
  }

  async updateCompanyQuestions(data: any): Promise<any> {
    const response = await this.getAuthenticatedClient().interviewSettings.updateCompanyQuestions(data);
    return response.data;
  }

  // Вопросы о компании для конкретной вакансии
  async getPositionCompanyQuestions(positionId: number): Promise<any> {
    const token = useAuthStore.getState().token;
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
    const response = await axios.get(`${baseUrl}/positions/${positionId}/company-questions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }

  async updatePositionCompanyQuestions(positionId: number, data: { questions: Array<{ question: string; answer: string; orderIndex?: number }> }): Promise<any> {
    const token = useAuthStore.getState().token;
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
    const response = await axios.put(`${baseUrl}/positions/${positionId}/company-questions`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }

  // ===== PDF REPORTS METHODS =====

  /**
   * Получить PDF отчет результата собеседования
   * @param interviewId ID собеседования
   * @returns Promise<Blob> PDF файл
   */
  async getInterviewResultPDF(interviewId: number): Promise<Blob> {
    try {
      console.log('🔍 getInterviewResultPDF: Запрос PDF отчета для интервью:', interviewId);
      
      // Используем прямой axios запрос для правильной обработки бинарных данных
      const token = useAuthStore.getState().token;
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const url = `${baseUrl}/reports/interviews/${interviewId}/result`;
      
      console.log('🔍 getInterviewResultPDF: URL запроса:', url);
      
      // Сначала получим данные интервью для отладки статуса
      try {
        const interviewData = await this.getInterview(interviewId);
        console.log('🔍 getInterviewResultPDF: Данные интервью для отладки:', {
          id: interviewData.id,
          status: interviewData.status,
          result: interviewData.result,
          score: interviewData.score,
          finishedAt: interviewData.finishedAt,
          createdAt: interviewData.createdAt
        });
      } catch (debugError) {
        console.warn('🔍 getInterviewResultPDF: Не удалось получить данные интервью для отладки:', debugError);
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        },
        responseType: 'blob' // Критически важно для PDF файлов
      });
      
      console.log('🔍 getInterviewResultPDF: Ответ получен:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: typeof response.data,
        dataSize: response.data?.size || 'unknown'
      });
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Получен пустой PDF файл');
      }
      
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении PDF отчета собеседования:', error);
      throw new Error(`Не удалось получить PDF отчет: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  }

  /**
   * Получить PDF отчет списка собеседований
   * @param filters Фильтры для отчета
   * @returns Promise<Blob> PDF файл
   */
  async getInterviewsListPDF(filters: {
    positionId?: number;
    candidateId?: number;
    status?: string;
    result?: string;
    search?: string;
    page?: number;
    size?: number;
    sort?: string;
    maxRows?: number;
  } = {}): Promise<Blob> {
    try {
      console.log('🔍 getInterviewsListPDF: Запрос PDF отчета списка собеседований с фильтрами:', filters);
      
      // Используем прямой axios запрос для правильной обработки бинарных данных
      const token = useAuthStore.getState().token;
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const url = `${baseUrl}/reports/interviews`;
      
      console.log('🔍 getInterviewsListPDF: URL запроса:', url);
      
      // Строим URL с параметрами (поддержка относительных путей через origin)
      const urlWithParams = new URL(url, window.location.origin);
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlWithParams.searchParams.append(key, String(value));
        }
      });
      
      console.log('🔍 getInterviewsListPDF: Полный URL с параметрами:', urlWithParams.toString());
      
      // Используем fetch вместо axios для лучшей обработки blob данных
      const response = await fetch(urlWithParams.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      console.log('🔍 getInterviewsListPDF: Fetch response получен:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        blobSize: blob.size,
        blobType: blob.type
      });
      
      // Дополнительная отладка blob данных
      if (blob) {
        console.log('🔍 getInterviewsListPDF: Blob детали:', {
          size: blob.size,
          type: blob.type,
          constructor: blob.constructor.name,
          isBlob: blob instanceof Blob
        });
        
        // Проверяем первые байты blob для диагностики
        const arrayBuffer = await blob.slice(0, 10).arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        console.log('🔍 getInterviewsListPDF: Первые 10 байт blob:', Array.from(uint8Array));
        
        // Проверяем, является ли это PDF (должно начинаться с %PDF)
        const textDecoder = new TextDecoder('utf-8', { fatal: false });
        const firstBytes = textDecoder.decode(arrayBuffer);
        console.log('🔍 getInterviewsListPDF: Первые байты как текст:', firstBytes);
      }
      
      if (!blob || blob.size === 0) {
        throw new Error('Получен пустой PDF файл');
      }
      
      return blob;
    } catch (error) {
      console.error('Ошибка при получении PDF отчета списка собеседований:', error);
      throw new Error(`Не удалось получить PDF отчет: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  }

  /**
   * Получить PDF отчёт итогов подбора.
   * Данные берутся из backend provider'а /stats/results: успешные AI с учётом minScore и live approved/completed.
   */
  async getRecruitmentResultsPDF(filters: RecruitmentResultsReportFilters = {}): Promise<Blob> {
    try {
      const token = useAuthStore.getState().token;
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const urlWithParams = new URL(`${baseUrl}/reports/recruitment-results`, window.location.origin);
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          urlWithParams.searchParams.append(key, String(value));
        }
      });

      const response = await fetch(urlWithParams.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Получен пустой PDF файл');
      }

      return blob;
    } catch (error) {
      console.error('Ошибка при получении PDF отчёта итогов подбора:', error);
      throw new Error(`Не удалось получить PDF отчёт итогов подбора: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  }

  /**
   * Скачать PDF отчет результата собеседования
   * @param interviewId ID собеседования
   * @param filename Имя файла для скачивания
   */
  async downloadInterviewResultPDF(interviewId: number, filename?: string): Promise<void> {
    try {
      console.log('🔍 downloadInterviewResultPDF: Начало скачивания PDF для интервью:', interviewId);
      
      const pdfBlob = await this.getInterviewResultPDF(interviewId);
      
      console.log('🔍 downloadInterviewResultPDF: PDF blob получен:', {
        size: pdfBlob.size,
        type: pdfBlob.type
      });
      
      // Создаем URL для blob
      const url = URL.createObjectURL(pdfBlob);
      console.log('🔍 downloadInterviewResultPDF: URL создан:', url);
      
      // Создаем ссылку для скачивания
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `interview-result-${interviewId}.pdf`;
      
      console.log('🔍 downloadInterviewResultPDF: Ссылка создана:', {
        href: link.href,
        download: link.download
      });
      
      // Добавляем ссылку в DOM и кликаем
      document.body.appendChild(link);
      link.click();
      
      // Очищаем
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('🔍 downloadInterviewResultPDF: Скачивание завершено');
      
    } catch (error) {
      console.error('Ошибка при скачивании PDF отчета:', error);
      throw error;
    }
  }

  /**
   * Скачать PDF отчет списка собеседований
   * @param filters Фильтры для отчета
   * @param filename Имя файла для скачивания
   */
  async downloadInterviewsListPDF(filters: any = {}, filename?: string): Promise<void> {
    try {
      console.log('🔍 downloadInterviewsListPDF: Начало скачивания PDF списка собеседований с фильтрами:', filters);
      
      const pdfBlob = await this.getInterviewsListPDF(filters);
      
      console.log('🔍 downloadInterviewsListPDF: PDF blob получен:', {
        size: pdfBlob.size,
        type: pdfBlob.type,
        constructor: pdfBlob.constructor.name,
        isBlob: pdfBlob instanceof Blob
      });
      
      // Дополнительная проверка blob перед созданием URL
      if (pdfBlob.size === 0) {
        throw new Error('PDF blob пустой');
      }
      
      // Проверяем первые байты для диагностики
      try {
        const arrayBuffer = await pdfBlob.slice(0, 10).arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        console.log('🔍 downloadInterviewsListPDF: Первые 10 байт blob:', Array.from(uint8Array));
        
        const textDecoder = new TextDecoder('utf-8', { fatal: false });
        const firstBytes = textDecoder.decode(arrayBuffer);
        console.log('🔍 downloadInterviewsListPDF: Первые байты как текст:', firstBytes);
      } catch (debugError) {
        console.warn('🔍 downloadInterviewsListPDF: Ошибка при отладке blob:', debugError);
      }
      
      // Создаем URL для blob
      const url = URL.createObjectURL(pdfBlob);
      console.log('🔍 downloadInterviewsListPDF: URL создан:', url);
      
      // Создаем ссылку для скачивания
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `interviews-list-${new Date().toISOString().split('T')[0]}.pdf`;
      
      console.log('🔍 downloadInterviewsListPDF: Ссылка создана:', {
        href: link.href,
        download: link.download
      });
      
      // Добавляем ссылку в DOM и кликаем
      document.body.appendChild(link);
      link.click();
      
      // Очищаем
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('🔍 downloadInterviewsListPDF: Скачивание завершено');
      
    } catch (error) {
      console.error('Ошибка при скачивании PDF отчета:', error);
      throw error;
    }
  }

  /**
   * Скачать PDF отчёт итогов подбора.
   * @param filters Фильтры итогов подбора
   * @param filename Имя файла для скачивания
   */
  async downloadRecruitmentResultsPDF(filters: RecruitmentResultsReportFilters = {}, filename?: string): Promise<void> {
    try {
      const pdfBlob = await this.getRecruitmentResultsPDF(filters);

      if (pdfBlob.size === 0) {
        throw new Error('PDF blob пустой');
      }

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `recruitment-results-${new Date().toISOString().split('T')[0]}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при скачивании PDF отчёта итогов подбора:', error);
      throw error;
    }
  }


  // === COMPLAINTS (PUBLIC ENDPOINT) ===
  async createComplaint(payload: { senderEmail: string; text: string }): Promise<{ id: number; status: string }> {
    try {
      const response = await this.post('/complaints', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data as { id: number; status: string };
    } catch (error: any) {
      // Пробрасываем для отображения сообщений об ошибке на форме
      throw error;
    }
  }

}

// Экспортируем единственный экземпляр сервиса
export const apiService = new ApiService(); 
